import type { ActivityStatus, ValidationStatus, DocumentStatus } from '@/types'

// =============================================================================
// Activity Status Badge
// =============================================================================

const ACTIVITY_STATUS_CONFIG: Record<ActivityStatus, { label: string; className: string }> = {
  NO_INICIADA:          { label: 'No iniciada',        className: 'bg-gray-100 text-gray-600 border-gray-200' },
  PLANIFICADA:          { label: 'Planificada',         className: 'bg-slate-100 text-slate-600 border-slate-200' },
  EN_CURSO:             { label: 'En curso',            className: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  FINALIZADA:           { label: 'Finalizada',          className: 'bg-blue-50 text-blue-700 border-blue-200' },
  BLOQUEADA:            { label: 'Bloqueada',           className: 'bg-red-50 text-red-700 border-red-200' },
  PENDIENTE_DOCUMENTAL: { label: 'Pend. documental',   className: 'bg-purple-50 text-purple-700 border-purple-200' },
}

const VALIDATION_STATUS_CONFIG: Record<ValidationStatus, { label: string; className: string }> = {
  PENDIENTE:   { label: 'Pendiente',   className: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  APROBADO:    { label: 'Aprobado',    className: 'bg-green-50 text-green-700 border-green-200' },
  RECHAZADO:   { label: 'Rechazado',   className: 'bg-red-50 text-red-700 border-red-200' },
  EN_REVISION: { label: 'En revisión', className: 'bg-orange-50 text-orange-700 border-orange-200' },
}

const DOCUMENT_STATUS_CONFIG: Record<DocumentStatus, { label: string; className: string }> = {
  PENDIENTE:   { label: 'Pendiente',   className: 'bg-gray-100 text-gray-600 border-gray-200' },
  EN_REVISION: { label: 'En revisión', className: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  APROBADO:    { label: 'Aprobado',    className: 'bg-green-50 text-green-700 border-green-200' },
  RECHAZADO:   { label: 'Rechazado',   className: 'bg-red-50 text-red-700 border-red-200' },
}

interface StatusBadgeProps {
  status:   ActivityStatus | ValidationStatus | DocumentStatus
  type?:    'activity' | 'validation' | 'document'
  size?:    'sm' | 'md'
  dot?:     boolean
}

export default function StatusBadge({
  status,
  type   = 'activity',
  size   = 'md',
  dot    = false,
}: StatusBadgeProps) {
  const config =
    type === 'validation'
      ? VALIDATION_STATUS_CONFIG[status as ValidationStatus]
      : type === 'document'
      ? DOCUMENT_STATUS_CONFIG[status as DocumentStatus]
      : ACTIVITY_STATUS_CONFIG[status as ActivityStatus]

  if (!config) return null

  const sizeClass = size === 'sm'
    ? 'text-[10px] px-1.5 py-0.5'
    : 'text-xs px-2.5 py-1'

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 font-medium rounded-full border whitespace-nowrap
        ${sizeClass} ${config.className}
      `}
    >
      {dot && (
        <span className="w-1.5 h-1.5 rounded-full bg-current flex-shrink-0" />
      )}
      {config.label}
    </span>
  )
}

// =============================================================================
// Severity Badge (for alerts)
// =============================================================================

const SEVERITY_CONFIG: Record<string, { label: string; className: string }> = {
  HIGH:   { label: 'Alta',   className: 'bg-red-50 text-red-700 border-red-200' },
  MEDIUM: { label: 'Media',  className: 'bg-orange-50 text-orange-700 border-orange-200' },
  LOW:    { label: 'Baja',   className: 'bg-blue-50 text-blue-700 border-blue-200' },
}

export function SeverityBadge({ severity }: { severity: string }) {
  const config = SEVERITY_CONFIG[severity]
  if (!config) return null

  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full border px-2.5 py-1 ${config.className}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {config.label}
    </span>
  )
}

// =============================================================================
// Role Badge
// =============================================================================

export function RoleBadge({ role }: { role: string }) {
  const colors: Record<string, string> = {
    ADMIN:              'bg-purple-50 text-purple-700 border-purple-200',
    SUPERVISOR_COBRA:   'bg-blue-50 text-blue-700 border-blue-200',
    SUBCONTRATISTA:     'bg-green-50 text-green-700 border-green-200',
    CONSULTA_CLIENTE:   'bg-gray-100 text-gray-600 border-gray-200',
  }

  const labels: Record<string, string> = {
    ADMIN:              'Admin',
    SUPERVISOR_COBRA:   'Supervisor',
    SUBCONTRATISTA:     'Subcontratista',
    CONSULTA_CLIENTE:   'Consulta',
  }

  return (
    <span className={`inline-flex text-xs font-medium rounded-full border px-2.5 py-1 ${colors[role] ?? colors.CONSULTA_CLIENTE}`}>
      {labels[role] ?? role}
    </span>
  )
}
